// Shared geometry helpers used by useAutoMark, importGoogleTakeout, and pathHistory.

// Ray-casting point-in-polygon. x=lng, y=lat. ring = [[lng,lat],...]
function pipRing(x, y, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

// Point-in-polygon for a GeoJSON geometry. Handles Polygon (with holes) and MultiPolygon.
export function pipGeometry(x, y, geom) {
  if (!geom) return false;
  if (geom.type === 'Polygon') {
    if (!pipRing(x, y, geom.coordinates[0])) return false;
    for (let h = 1; h < geom.coordinates.length; h++) if (pipRing(x, y, geom.coordinates[h])) return false;
    return true;
  }
  if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) {
      if (pipRing(x, y, poly[0])) {
        let inHole = false;
        for (let h = 1; h < poly.length; h++) if (pipRing(x, y, poly[h])) { inHole = true; break; }
        if (!inHole) return true;
      }
    }
  }
  return false;
}

// Perpendicular distance from point p to the line through a–b (in degrees).
function perpDist(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = p[0] - a[0], ey = p[1] - a[1];
    return Math.sqrt(ex * ex + ey * ey);
  }
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  const px = p[0] - (a[0] + t * dx), py = p[1] - (a[1] + t * dy);
  return Math.sqrt(px * px + py * py);
}

// Ramer-Douglas-Peucker: keeps the points that define the polyline's shape
// and drops points that lie (nearly) on the straight line between their
// neighbors. First + last are always preserved.
function simplifyDP(coords, tol) {
  const n = coords.length;
  if (n < 3) return coords.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1; keep[n - 1] = 1;
  const stack = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let maxD = 0, idx = -1;
    const a = coords[s], b = coords[e];
    for (let i = s + 1; i < e; i++) {
      const d = perpDist(coords[i], a, b);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > tol && idx >= 0) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(coords[i]);
  return out;
}

function simplifyRing(ring, tolerance) {
  if (!ring || ring.length < 5) return ring;
  const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
  const open = closed ? ring.slice(0, -1) : ring;
  const simplified = simplifyDP(open, tolerance);
  if (simplified.length < 3) return ring;
  return closed ? [...simplified, simplified[0]] : simplified;
}

function simplifyRings(rings, tolerance) {
  return rings.map((ring) => simplifyRing(ring, tolerance));
}

export function simplifyGeometry(geometry, tolerance = 0.0025) {
  if (!geometry) return geometry;
  if (geometry.type === 'Polygon') {
    return { ...geometry, coordinates: simplifyRings(geometry.coordinates, tolerance) };
  }
  if (geometry.type === 'MultiPolygon') {
    return { ...geometry, coordinates: geometry.coordinates.map((polygon) => simplifyRings(polygon, tolerance)) };
  }
  return geometry;
}

export function simplifyFeatureCollection(data, tolerance = 0.0025) {
  if (!data?.features) return data;
  return {
    ...data,
    features: data.features.map((feature) => ({
      ...feature,
      geometry: simplifyGeometry(feature.geometry, tolerance),
    })),
  };
}

// Shape-preserving downsample: uses Douglas-Peucker so curves keep their
// detail while straight sections collapse to a few points — far better than
// even-stride sampling for the same point budget. Binary-searches the
// tolerance to get as close to maxPoints as possible.
export function downsample(coords, maxPoints = 200) {
  if (!coords || coords.length <= maxPoints) return coords;
  // Find a tolerance range: lo gives > maxPoints, hi gives <= maxPoints.
  let lo = 0, hi = 0.0001;
  let rLo = simplifyDP(coords, lo);
  if (rLo.length <= maxPoints) return rLo;
  let rHi = simplifyDP(coords, hi);
  while (rHi.length > maxPoints) {
    lo = hi; rLo = rHi;
    hi *= 4;
    rHi = simplifyDP(coords, hi);
    if (hi > 1e6) return rHi; // safety valve — shouldn't reach
  }
  // Binary-search between lo and hi to land just under the budget.
  for (let i = 0; i < 16 && hi - lo > 1e-14; i++) {
    const mid = (lo + hi) / 2;
    const r = simplifyDP(coords, mid);
    if (r.length > maxPoints) { lo = mid; rLo = r; }
    else { hi = mid; rHi = r; }
  }
  return rHi;
}